/* global google */

import * as I from "immutable";
import Raven from "raven-js";
import React, {Component} from "react";
import PropTypes from "prop-types";

import {trackEvent} from "metrics";
import {formatDuration} from "utils";

import api from "v2/api";
import {AppData, Route} from "v2/api/Records";
import Empty from "v2/components/Empty";
import ReorderDestinations from "v2/components/dnd/ReorderDestinations";
import ModalForm from "v2/components/responsive/ModalForm";
import {directionServiceRoute} from "v1/googleUtils";
import {
  generateDrivingTimeRequests,
  updateDestinationsTimes,
} from "v2/googleUtils";

export default class ReorderDestinationsForm extends Component {
  static contextTypes = {
    onUpdateRouteData: PropTypes.func,
    params: PropTypes.shape({
      routeId: PropTypes.string.isRequired,
    }),
    routeData: PropTypes.instanceOf(Route),
    appData: PropTypes.instanceOf(AppData),
  }
  static propTypes = {
    onCancel: PropTypes.func,
  }

  constructor(props, context) {
    super(props, context);

    const {routeData} = context;

    this.state = {
      destinations: routeData.destinations,
      errors: I.Map(),
      googleError: false,
      submitted: false,
      timesLoading: false,
    };
  }

  handleBeginDrag = () => {
    this.setState(({destinations}) => ({
      previousDestinations: destinations,
      timesLoading: true,
    }));
  }

  handleEndDrag = () => {
    const {appData} = this.context;
    const {destinations, previousDestinations} = this.state;
    if (I.is(destinations, previousDestinations)) {
      this.setState({
        timesLoading: false,
      });
    }

    const {routeData} = this.context;
    const requests = generateDrivingTimeRequests(destinations);

    Promise
      .all(requests.map(directionServiceRoute))
      .then((responses) => {
        this.setState(({destinations}) => ({
          destinations: updateDestinationsTimes(
            responses,
            destinations,
            routeData.destinations.first().estStartTime,
            appData),
          googleError: false,
          timesLoading: false,
        }));
      })
      .catch(() => {
        this.setState({
          googleError: true,
          timesLoading: false,
        });
      });
  }

  handleMoveDestination = (id, afterId) => {
    this.setState(({destinations}) => {
      const destination = destinations.find(item => item.id === id);
      const afterDestination = destinations.find(item => item.id === afterId);

      if (!destination || !afterDestination) {
        return {};
      }

      const newDestinations = destinations
        .splice(destinations.indexOf(destination), 1)
        .splice(destinations.indexOf(afterDestination), 0, destination);

      return {
        destinations: newDestinations.map((destination, idx) => {
          return destination.set("slot", idx + 1);
        }),
      };
    });
  }

  handleSubmit = (evt) => {
    evt.preventDefault();
    this.setState({
      submitted: true,
    });

    const {
      onUpdateRouteData,
      params: {date, routeId},
      routeData,
    } = this.context;
    const {onCancel} = this.props;
    const {destinations} = this.state;

    api()
      .patch(this.apiUrl, {
        destinations: destinations
          .map(({duration, id, estStartTime}) => ({
            id,
            "estimated_start_time": estStartTime.toISOString(),
            duration: formatDuration(duration),
          }))
          .toArray(),
      }, {
        validateStatus: (status) => status === 204,
      })
      .then(() => {
        onUpdateRouteData(null, () => {
          onCancel();
          trackEvent("Reorder Destinations", ["Route", "reorder", routeId], {
            Date: date,
            ID: routeId,
            Type: routeData.type,
          });
        });
      })
      .catch((err) => {
        if (err.response) {
          this.setState({
            errors: I.Map(err.response.data),
            submitted: false,
          });
        } else {
          Raven.captureException(err);
        }
      });
  }

  get apiUrl() {
    const {params: {routeId}} = this.context;
    return `/routes/${routeId}/destinations/rearrange`;
  }

  renderContent() {
    if (!google) {
      return (
        <Empty title="Error">
          Unable to reorder destinations as Google API not loaded. Please
          refresh the page and try againg.
        </Empty>
      );
    }

    const {destinations, googleError, timesLoading} = this.state;
    if (destinations.filter(item => item.address).count() < 2) {
      return (
        <Empty title="Warning">
          Route contain only one Destination with address, nothing to reorder.
        </Empty>
      );
    }

    return (
      <div>
        <ReorderDestinations
          data={destinations}
          onBeginDrag={this.handleBeginDrag}
          onEndDrag={this.handleEndDrag}
          onMove={this.handleMoveDestination}
          timesLoading={timesLoading} />
        {googleError &&
          <p className="text-danger small erg-margin-top-15">
            Unable to reorder destinations: one or more of your destinations
            has a bad address that&apos;s not supported by Google Maps.
          </p>}
        <p className="text-muted small erg-margin-top-15">
          Destinations without address are muted.
        </p>
      </div>
    );
  }

  render() {
    const {routeData: {destinations: originalDestinations}} = this.context;
    const {destinations, googleError, submitted} = this.state;
    const sameDestinations = I.is(destinations, originalDestinations);
    return (
      <ModalForm
        btnSubmitDisabled={submitted || sameDestinations || googleError}
        btnSubmitLabel="Reorder"
        modalBodyClassName="erg-padding-15"
        modalDialogClassName="ModalLarge"
        modalTitle="Reorder Destinations"
        onSubmit={this.handleSubmit}
        {...this.props}>
        {this.renderContent()}
      </ModalForm>
    );
  }
}



/** WEBPACK FOOTER **
 ** ./src/app/v2/components/responsive/ReorderDestinationsForm.js
 **/